import { useMemo } from 'react';

import { SubscribePlan } from '../../../../types';
import { PlanPaymentMethodType, PlanPrices } from '../types';

const DEFAULT_SALE_RATE_UNDER_20_MAXCCU = 50;

/**
 * 정기, 일시납 플랜에서 보여줄 인당 가격과 할인율, 할인 가격 등을 계산하는 훅입니다.
 * 인자인 planType이 enterprise가 선택될 수 있으므로 해당 경우에는 모든 값을 0으로 반환합니다.
 * maxCcu가 20 이하일 때 모든 saleRate는 50%가 기본값으로 설정됩니다.
 * @param planType
 * @param prices
 * @param maxCcu
 */
export const usePlanPricing = ({
  planType,
  prices,
  maxCcu,
}: UsePlanPricingProps): UsePlanPricingResult => {
  const { monthly, yearly, oneDay, threeDay, customMonth } = prices;

  const monthlySaleRate =
    maxCcu <= 20 ? DEFAULT_SALE_RATE_UNDER_20_MAXCCU : monthly?.saleRate || 0;
  const yearlySaleRate =
    maxCcu <= 20 ? DEFAULT_SALE_RATE_UNDER_20_MAXCCU : yearly?.saleRate || 0;
  const oneDaySaleRate =
    maxCcu <= 20 ? DEFAULT_SALE_RATE_UNDER_20_MAXCCU : oneDay?.saleRate || 0;
  const threeDaySaleRate =
    maxCcu <= 20 ? DEFAULT_SALE_RATE_UNDER_20_MAXCCU : threeDay?.saleRate || 0;
  const customMonthSaleRate =
    maxCcu <= 20
      ? DEFAULT_SALE_RATE_UNDER_20_MAXCCU
      : customMonth?.saleRate || 0;

  // planType이 없으면 모든 값을 0으로 반환
  const monthlyPrice = planType
    ? monthly[planType as keyof Omit<PlanPrices, 'saleRate'>] || 0
    : 0;
  const yearlyPrice = planType
    ? yearly[planType as keyof Omit<PlanPrices, 'saleRate'>] || 0
    : 0;
  const oneDayPrice = planType
    ? oneDay[planType as keyof Omit<PlanPrices, 'saleRate'>] || 0
    : 0;
  const threeDayPrice = planType
    ? threeDay[planType as keyof Omit<PlanPrices, 'saleRate'>] || 0
    : 0;
  const customMonthPrice = planType
    ? customMonth[planType as keyof Omit<PlanPrices, 'saleRate'>] || 0
    : 0;

  const finalMonthlyPrice = useMemo(() => {
    return monthlyPrice * (1 - monthlySaleRate * 0.01);
  }, [monthlyPrice, monthlySaleRate]);

  const finalYearlyPrice = useMemo(() => {
    return yearlyPrice * (1 - yearlySaleRate * 0.01);
  }, [yearlyPrice, yearlySaleRate]);

  const finalOneDayPrice = useMemo(() => {
    return oneDayPrice * (1 - oneDaySaleRate * 0.01);
  }, [oneDayPrice, oneDaySaleRate]);

  const finalThreeDayPrice = useMemo(() => {
    return threeDayPrice * (1 - threeDaySaleRate * 0.01);
  }, [threeDayPrice, threeDaySaleRate]);

  const finalCustomMonthPrice = useMemo(() => {
    return customMonthPrice * (1 - customMonthSaleRate * 0.01);
  }, [customMonthPrice, customMonthSaleRate]);

  return {
    monthlySaleRate,
    yearlySaleRate,
    oneDaySaleRate,
    threeDaySaleRate,
    customMonthSaleRate,
    monthlyPrice,
    yearlyPrice,
    oneDayPrice,
    threeDayPrice,
    customMonthPrice,
    finalMonthlyPrice,
    finalYearlyPrice,
    finalOneDayPrice,
    finalThreeDayPrice,
    finalCustomMonthPrice,
  };
};

interface UsePlanPricingProps {
  planType: Omit<SubscribePlan, 'free'>; // planType이 없을 수 있으므로 null 가능
  prices: PlanPaymentMethodType;
  maxCcu: number; // 추가된 maxCcu 프로퍼티
}

interface UsePlanPricingResult {
  oneDaySaleRate: number;
  threeDaySaleRate: number;
  customMonthSaleRate: number;
  oneDayPrice: number;
  threeDayPrice: number;
  customMonthPrice: number;
  finalMonthlyPrice: number;
  finalYearlyPrice: number;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlySaleRate: number;
  yearlySaleRate: number;
  finalOneDayPrice: number;
  finalThreeDayPrice: number;
  finalCustomMonthPrice: number;
}
